import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { t, tEn } from '../i18n/translations'
import { codeIsComplete, formatCodeInput, redeemErrorMessage, redeemTeacherCode } from '../lib/teacherCode'
import { clearPendingRedeem, setPendingRedeem } from '../lib/redeemEntry'

// The "Have a teacher code?" form, used by both the locked-lesson prompt and
// Settings. It submits the code and reports what came back; it never decides
// anything about access itself. On success the entitlement listener in
// AuthContext turns Premium on, so there is nothing to unlock here.
// showDismiss: Settings keeps its own collapsing row above this, so a second
// dismiss button there is redundant and reads oddly. The upgrade dialog has no
// such row and needs one.
export default function TeacherCodeForm({ onDone, showDismiss = true }) {
  const { user, login } = useAuth()
  const [code, setCode] = useState('')
  const [status, setStatus] = useState('idle')   // idle | working | done
  const [error, setError] = useState('')
  const [alsoPaying, setAlsoPaying] = useState(false)

  async function submit(event) {
    event.preventDefault()
    if (status === 'working' || !codeIsComplete(code)) return
    setStatus('working')
    setError('')
    try {
      const result = await redeemTeacherCode(code)
      clearPendingRedeem()
      setAlsoPaying(Boolean(result?.alreadyPaid))
      setStatus('done')
    } catch (err) {
      setError(redeemErrorMessage(err))
      setStatus('idle')
    }
  }

  if (status === 'done') {
    return (
      <div className="teacher-code" role="status">
        <p className="teacher-code-granted" title={tEn('teacherLifetimeTitle')}>
          {t('teacherLifetimeTitle')}
        </p>
        <p className="teacher-code-note" title={tEn('teacherLifetimeDesc')}>{t('teacherLifetimeDesc')}</p>
        {alsoPaying && (
          <p className="teacher-code-note teacher-code-warn" title={tEn('teacherAlsoPaying')}>
            {t('teacherAlsoPaying')}
          </p>
        )}
        <button className="upgrade-cta" onClick={onDone} title={tEn('continueFree')}>{t('browseContinue')}</button>
      </div>
    )
  }

  // Google sign-in falls back to a full-page redirect when a popup is blocked,
  // which is normal on phones. The flag is what reopens this dialog
  // afterwards; the code itself is never stored, so it is retyped.
  function signInKeepingPlace() {
    setPendingRedeem()
    login()
  }

  // Signing in has to come first: the grant is attached to an account.
  if (!user) {
    return (
      <div className="teacher-code">
        <p className="teacher-code-note" title={tEn('teacherCodeSignIn')}>{t('teacherCodeSignIn')}</p>
        <button className="upgrade-cta" onClick={signInKeepingPlace} title={tEn('alreadySubscribed')}>{t('signIn')}</button>
        {showDismiss && <button className="upgrade-secondary" onClick={onDone}>{t('continueFree')}</button>}
      </div>
    )
  }

  return (
    <form className="teacher-code" onSubmit={submit}>
      <label className="teacher-code-label" htmlFor="teacher-code-input" title={tEn('teacherCodeLabel')}>
        {t('teacherCodeLabel')}
      </label>
      <input
        id="teacher-code-input"
        className="teacher-code-input"
        value={code}
        onChange={e => setCode(formatCodeInput(e.target.value))}
        placeholder="XXXX-XXXX-XXXX-XXXX"
        autoComplete="off"
        spellCheck="false"
        inputMode="text"
        aria-describedby={error ? 'teacher-code-error' : undefined}
        aria-invalid={error ? 'true' : undefined}
      />
      {error && <p className="teacher-code-error" id="teacher-code-error" role="alert">{error}</p>}
      <button
        type="submit"
        className="upgrade-cta"
        disabled={status === 'working' || !codeIsComplete(code)}
        title={tEn('teacherCodeSubmit')}
      >
        {status === 'working' ? t('teacherCodeWorking') : t('teacherCodeSubmit')}
      </button>
      {showDismiss && (
        <button type="button" className="upgrade-secondary" onClick={onDone}>{t('teacherCodeCancel')}</button>
      )}
    </form>
  )
}
